import { useState } from "react";
import {
  LayoutGrid,
  NotebookPen,
  BookOpen,
  BarChart3,
  Calculator,
  CalendarDays,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageCircleQuestion,
  Award,
  Lock,
  Instagram, // Added for social links
  Youtube    // Added for social links
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import logo from "../assets/logo.png";

export default function Sidebar({ setTab, tab, hasMembership }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [openTrading, setOpenTrading] = useState(true);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const premiumTabs = ["addTrade", "journal", "dashboard", "lotCalculator", "news"];

  const Item = ({ id, label, Icon, onClick, active, indent, textColor }) => {
    const isLocked = premiumTabs.includes(id) && !hasMembership;

    return (
      <button
        onClick={onClick || (() => setTab(id))}
        className={active ? "nav-item-active" : "nav-item-hover"}
        style={{
          ...styles.item,
          paddingLeft: indent && !collapsed ? 36 : 14,
          justifyContent: collapsed ? "center" : "flex-start",
          background: active 
            ? "linear-gradient(90deg, rgba(56, 189, 248, 0.1) 0%, rgba(56, 189, 248, 0) 100%)" 
            : "transparent",
          color: active ? "#38BDF8" : (isLocked ? "#475569" : (textColor || "#94a3b8")),
          borderLeft: active ? "3px solid #38BDF8" : "3px solid transparent",
          borderTopLeftRadius: active ? 0 : 8,
          borderBottomLeftRadius: active ? 0 : 8,
          opacity: isLocked ? 0.6 : 1,
          cursor: isLocked && tab === id ? "default" : "pointer"
        }}
      >
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Icon 
            size={18} 
            strokeWidth={active ? 2.5 : 2} 
            style={{ transition: "0.3s ease", flexShrink: 0 }}
          />
          {isLocked && (
            <div style={{
              position: 'absolute',
              top: -6,
              right: -8,
              background: '#0f172a',
              borderRadius: '50%',
              padding: '1px'
            }}>
              <Lock size={10} color="#ef4444" strokeWidth={3} />
            </div>
          )}
        </div>
        
        {!collapsed && (
          <span style={{ 
            marginLeft: 12, 
            fontWeight: active ? 600 : 400,
            whiteSpace: "nowrap",
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {label}
          </span>
        )}
      </button>
    );
  };

  const Section = ({ title, Icon, open, toggle }) => (
    <button
      onClick={toggle}
      style={{
        ...styles.sectionHeader,
        justifyContent: collapsed ? "center" : "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Icon size={18} color="#38BDF8" style={{ flexShrink: 0 }} />
        {!collapsed && <span style={styles.sectionTitle}>{title}</span>}
      </div>
      {!collapsed && (
        <ChevronDown
          size={14}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.3s ease",
            color: "#475569"
          }}
        />
      )}
    </button>
  );

  return (
    <>
      <style>{sidebarCustomCSS}</style>
      <div
        style={{
          ...styles.sidebar,
          width: collapsed ? 80 : 280
        }}
      >
        <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
                ...styles.collapseBtn,
                right: -12,
            }}
            className="collapse-hover"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <div style={styles.logoWrapper}>
          <img
            src={logo}
            alt="Logo"
            style={{
              width: collapsed ? 45 : 140,
              filter: "drop-shadow(0 0 8px rgba(56, 189, 248, 0.15))",
              transition: "0.3s ease"
            }}
          />
        </div>

        <div className="no-scrollbar" style={styles.navScrollArea}>
          <div style={styles.navGroup}>
            <Section
              title="FOR TRADING"
              Icon={LayoutGrid}
              open={openTrading}
              toggle={() => setOpenTrading(!openTrading)}
            />

            <div style={{
              maxHeight: openTrading ? "500px" : "0px",
              overflow: "hidden",
              transition: "0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}>
              <Item id="accounts" label="Account Overview" Icon={LayoutGrid} active={tab === "accounts"} indent />
              <Item id="addTrade" label="Log Trades" Icon={NotebookPen} active={tab === "addTrade"} indent />
              <Item id="journal" label="Trade Journal" Icon={BookOpen} active={tab === "journal"} indent />
              <Item id="dashboard" label="Account Dashboard" Icon={BarChart3} active={tab === "dashboard"} indent />
              <Item id="lotCalculator" label="Lot Calculator" Icon={Calculator} active={tab === "lotCalculator"} indent />
              <Item id="news" label="News / Event" Icon={CalendarDays} active={tab === "news"} indent />
            </div>
          </div>
        </div>

        <div style={styles.bottomFixedSection}>
          
          {/* NEW: FOLLOW US SECTION */}
          {!collapsed && (
            <div style={{ padding: '0 14px', marginBottom: 12 }}>
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#475569', letterSpacing: '1px', marginBottom: 8 }}>FOLLOW US</div>
              <div style={{ height: '1px', background: 'linear-gradient(90deg, #1e293b, transparent)', marginBottom: 12 }} />
              <div style={{ display: 'flex', gap: 16 }}>
                <Instagram 
                  size={18} 
                  color="#94a3b8" 
                  style={{ cursor: 'pointer' }} 
                  className="social-hover"
                  onClick={() => window.open("https://www.instagram.com/focusedtraderjournal?igsh=NjVma2QxeDFxNWFy&utm_source=qr", "_blank")}
                />
                <Youtube 
                  size={18} 
                  color="#94a3b8" 
                  style={{ cursor: 'pointer' }} 
                  className="social-hover"
                  onClick={() => window.open("https://youtube.com/@thefocusedtraderr?si=_TbyeTfCcchNp59n", "_blank")}
                />
              </div>
            </div>
          )}

          {collapsed && (
             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Instagram size={18} color="#94a3b8" style={{ cursor: 'pointer' }} onClick={() => window.open("https://www.instagram.com/focusedtraderjournal", "_blank")} />
                <Youtube size={18} color="#94a3b8" style={{ cursor: 'pointer' }} onClick={() => window.open("https://youtube.com/@thefocusedtraderr", "_blank")} />
             </div>
          )}

          <div style={styles.divider} />
          
          <Item id="subscription" label="Join A Family 🏆" Icon={Award} active={tab === "subscription"} />
          <Item id="support" label="Support" Icon={MessageCircleQuestion} active={tab === "support"} />
          <Item label="Logout" Icon={LogOut} textColor="#ef4444" onClick={() => setShowLogoutConfirm(true)} />
        </div>
      </div>

      {showLogoutConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalIconBox}><LogOut size={24} color="#ef4444" /></div>
            <h3 style={styles.modalTitle}>Terminate Session?</h3>
            <p style={styles.modalText}>Ensure all active trades are logged to maintain your PnL accuracy.</p>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setShowLogoutConfirm(false)}>Stay Signed In</button>
              <button style={styles.confirmBtn} onClick={handleLogout}>Yes, Logout</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const sidebarCustomCSS = `
  .nav-item-hover { transition: all 0.2s ease; }
  .nav-item-hover:hover { 
    background: rgba(255, 255, 255, 0.03) !important; 
    color: #fff !important;
    transform: translateX(4px);
  }
  .social-hover:hover {
    color: #38BDF8 !important;
    transform: scale(1.1);
    transition: 0.2s ease;
  }
  .nav-item-active { 
    box-shadow: inset 4px 0 10px -4px rgba(56, 189, 248, 0.1);
  }
  .collapse-hover:hover {
    background: #38BDF8 !important;
    color: #000 !important;
    border-color: #38BDF8 !important;
    transform: translateY(-50%) scale(1.1);
  }
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
`;

const styles = {
  sidebar: { background: "transparent", display: "flex", flexDirection: "column", height: "100vh", borderRight: "1px solid #1e293b", transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)", position: 'relative', zIndex: 100 },
  logoWrapper: { padding: "32px 16px", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", marginBottom: 20 },
  collapseBtn: { position: "absolute", top: "50%", right: -12, transform: "translateY(-50%)", background: "#0f172a", border: "1px solid #1e293b", borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b", zIndex: 1001, transition: "0.2s ease", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)" },
  navScrollArea: { flex: 1, overflowY: "auto", padding: "0 12px", overflowX: "hidden" },
  navGroup: { display: "flex", flexDirection: "column" },
  sectionHeader: { height: 40, display: "flex", alignItems: "center", padding: "0 14px", borderRadius: 8, border: "none", background: "transparent", color: "#475569", cursor: "pointer", fontSize: 11, fontWeight: 800, letterSpacing: "1px", marginBottom: 4 },
  item: { height: 44, display: "flex", alignItems: "center", border: "none", cursor: "pointer", fontSize: 14, width: "100%", outline: 'none', marginBottom: 2, overflow: "hidden" },
  bottomFixedSection: { padding: "16px 12px 24px 12px", background: "transparent", marginTop: 'auto' },
  divider: { height: 1, background: "linear-gradient(90deg, transparent, #1e293b, transparent)", marginBottom: 16 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  modal: { width: 360, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 20, padding: 32, textAlign: "center" },
  modalIconBox: { width: 56, height: 56, background: "rgba(239, 68, 68, 0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" },
  modalTitle: { fontSize: 20, fontWeight: 800, color: "#f1f5f9", marginBottom: 12 },
  modalText: { fontSize: 14, lineHeight: 1.5, color: "#94a3b8", marginBottom: 28 },
  modalActions: { display: "flex", flexDirection: 'column', gap: 10 },
  cancelBtn: { padding: "12px", background: "#1e293b", border: "none", borderRadius: 10, color: "#f1f5f9", fontWeight: 600, cursor: "pointer" },
  confirmBtn: { padding: "12px", background: "#ef4444", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer" }
};